import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Box, Grid, Typography, Avatar, IconButton, CircularProgress, Button, TextField, MenuItem, Select, InputLabel, FormControl } from '@mui/material';
import { ArrowUpward } from '@mui/icons-material';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/system';
import GenerateOTPButtonRequestUser from '../OTP/GenerateOTPButtonRequestUser';
import FeedbackFormRequestFromUser from '../Feedback/FeedbackFormRequestFromUser';
import YouTubeEmbed from '../UserProfile/YouTubeEmbed';
import CancelSessionRequestUserButton from '../Cancellations/CancellSessionRequestUserButton';
import { LinkedIn } from '@mui/icons-material';
import GoogleMeetLogo from '../Post/GoogleMeetImage.png';
import ZoomMeetLogo from '../Post/ZoomMeetImage.png';
import TeamsMeetLogo from '../Post/TeamsMeetImage.png';
import { Dialog, DialogActions, DialogContent, DialogTitle, Checkbox, FormControlLabel } from '@mui/material';
import MeetingGuidelines from '../Guidelines/MeetingGuidelines';




const API_URL = process.env.REACT_APP_API_URL;

const RequestFromUserTabDetails = ({ onLogout, userId }) => {

  useEffect(() => {
    window.scrollTo(0, 0);  // Scroll to top when this page is loaded
  }, []);


  const { id, student_id } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [requestData, setRequestData] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [meetingLink, setMeetingLink] = useState('');
  const [isValid, setIsValid] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {

    const fetchData = async () => {
      try {
        // Fetch user data
        const userResponse = await axios.get(`${API_URL}/get_user_data/`, { params: { user_id: student_id } });
        setUserData(userResponse.data);

        // Fetch request data
        const requestResponse = await axios.get(`${API_URL}/request-user/${id}/`);
        setRequestData(requestResponse.data);

        setLoading(false);
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    };

    fetchData();
  }, [id, student_id]);


  const getLogo = (platform) => {
    switch (platform) {
      case 'googleMeet':
        return GoogleMeetLogo;
      case 'zoom':
        return ZoomMeetLogo;
      case 'microsoftTeams':
        return TeamsMeetLogo;
      default:
        return null; // Optionally, you can return a default image or a placeholder here
    }
  };


  const getPlatformName = (platform) => {
    switch (platform) {
      case 'googleMeet':
        return 'Google Meet';
      case 'zoom':
        return 'Zoom';
      case 'microsoftTeams':
        return 'Microsoft Teams';
      default:
        return null;
    }
  };


  const [openDialog, setOpenDialog] = useState(false); // To control the dialog's visibility
  const [isChecked, setIsChecked] = useState(false); // To track checkbox state

  const handleJoinClick = () => {
    setOpenDialog(true); // Open the dialog
  };

  const handleCheckboxChange = (event) => {
    setIsChecked(event.target.checked); // Update checkbox state
  };

  const handleConfirmAndJoin = () => {
    if (isChecked) {
      // Use navigate to redirect to the meeting link
      window.open(requestData.meeting_link, '_blank'); // Open in a new tab
      setOpenDialog(false); // Close the dialog
    } else {
      alert('Please confirm that you understand the instructions.');
    }
  };


  const validateMeetingLink = (link, platform) => {
    const googleMeetRegex = /https:\/\/meet.google.com\/[a-zA-Z0-9\-]+/;

    const zoomRegex = /https:\/\/\w+\.zoom.us\/j\/\d+(\?pwd=[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=]*)?/;  // Updated regex

    const teamsRegex = /https:\/\/teams\.live\.com\/meet\/[a-zA-Z0-9\-]+/;

    switch (platform) {
      case "googleMeet":
        return googleMeetRegex.test(link);
      case "zoom":
        return zoomRegex.test(link);
      case "microsoftTeams":
        return teamsRegex.test(link);
      default:
        return false;
    }
  };



  // Handle platform change
  const handlePlatformChange = (e) => {
    const selectedPlatform = e.target.value;
    setSelectedPlatform(selectedPlatform);

    // Validate the link immediately when platform is selected
    if (meetingLink && meetingLink.trim() !== "") {
      const isLinkValid = validateMeetingLink(meetingLink, selectedPlatform);
      setIsValid(isLinkValid);
      if (!isLinkValid) {
        setErrorMessage("Invalid link for the selected platform.");
      } else {
        setErrorMessage("");
      }
    } else {
      // Reset validation when link is empty
      setIsValid(true);
      setErrorMessage("");
    }
  };


  // Handle link change
  const handleLinkChange = (e) => {
    const link = e.target.value;
    setMeetingLink(link);

    // If the link is empty, reset validation and error message
    if (link.trim() === "") {
      setIsValid(true); // Set valid state as true when the link is empty
      setErrorMessage(""); // Reset the error message
    } else if (selectedPlatform) {
      // Validate the link immediately when user types/pastes it
      const isLinkValid = validateMeetingLink(link, selectedPlatform);
      setIsValid(isLinkValid);
      if (!isLinkValid) {
        setErrorMessage("Invalid link for the selected platform.");
      } else {
        setErrorMessage("");
      }
    }
  };

  const renderPlatformInfo = () => {
    switch (selectedPlatform) {
      case 'googleMeet':
        return (
          <Box textAlign="center">
            {/* <GoogleMeetIcon/> */}
            <img src={GoogleMeetLogo} alt="Google Meet" width={100} />
            <Typography variant="h6">Google Meet</Typography>
          </Box>
        );
      case 'zoom':
        return (
          <Box textAlign="center">
            <img src={ZoomMeetLogo} alt="Zoom" width={100} />
            <Typography variant="h6">Zoom</Typography>
          </Box>
        );
      case 'microsoftTeams':
        return (
          <Box textAlign="center">
            <img src={TeamsMeetLogo} alt="Microsoft Teams" width={100} />
            <Typography variant="h6">Microsoft Teams</Typography>
          </Box>
        );
      default:
        return <Typography variant="body1" sx={{ letterSpacing: 2 }}>Select a platform to see details.</Typography>;
    }
  };



  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRedirect = () => {
    navigate('/meeting-guidelines');  // Redirect to meeting guidelines page
  };

  const handleAccept = (requestId) => {
    if (!meetingLink || meetingLink.trim() === '') {
      alert("Please provide a valid meeting link before accepting the request.");
      return; // Stop execution if the link is empty
    }

    axios.post(`${API_URL}/accept_request/`, {
      request_id: requestId,
      platform: selectedPlatform,
      meeting_link: meetingLink
    })
      .then(response => {
        setRequests((prevRequests) =>
          prevRequests.map((request) =>
            request.id === requestId
              ? {
                ...request,
                status: 'Accepted',
                platform: selectedPlatform,
                meeting_link: meetingLink,
              }
              : request
          )
        );
        showAlert();
        window.location.reload();
      })
      .catch(error => {
        console.error('Error accepting request:', error);
      });
  };


  const handleReject = (requestId) => {
    // Handle Reject logic, update status via API
    axios.post(`${API_URL}/reject_request/`, { request_id: requestId })
      .then(response => {
        setRequests((prevRequests) =>
          prevRequests.map((request) =>
            request.id === requestId ? { ...request, status: 'Rejected' } : request
          )
        );
        alert('Request rejected Successfully');
        window.location.reload();
      })
      .catch(error => {
        console.error('Error rejecting request:', error);
      });
  };

  const showAlert = () => {
    const alertDiv = document.createElement('div');
    alertDiv.textContent = 'Request Accepted successfully';
    alertDiv.style.position = 'fixed';
    alertDiv.style.top = '10px';
    alertDiv.style.left = '50%';
    alertDiv.style.transform = 'translateX(-50%)';
    alertDiv.style.padding = '10px 20px';
    alertDiv.style.backgroundColor = 'green';
    alertDiv.style.color = 'white';
    alertDiv.style.borderRadius = '5px';
    alertDiv.style.zIndex = '9999';
    alertDiv.style.fontSize = '16px';

    document.body.appendChild(alertDiv);

    // Auto remove the alert after 3 seconds
    setTimeout(() => {
      alertDiv.remove();
    }, 3000);
  };

  return (
    <div>
      <Box sx={{ backgroundColor: '#000000', mt: 10 }}>
        <Header1 userId={userId} onLogout={onLogout} />
        <Typography
          variant="h3"
          component="h1"
          align="center"
          fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
          sx={{
            backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
            mt: 10,
            letterSpacing: 10,
            WebkitBackgroundClip: 'text', // This makes the gradient clip to the text
            color: 'transparent', // Makes the text itself transparent to show the gradient
          }}
          gutterBottom
        >
          Request Recieved from {userData?.name ? `${userData.name} ${userData.surname}` : 'No name available'}
        </Typography>

        <Box sx={{ padding: 4 }}>
          <IconButton onClick={scrollToTop} sx={{ position: 'fixed', bottom: 16, right: 16, backgroundColor: 'white', color: 'black' }}>
            <ArrowUpward />
          </IconButton>

          {loading ? (
            <CircularProgress sx={{ display: 'block', margin: 'auto', paddingTop: '50px' }} />
          ) : (
            <Grid container spacing={4}>
              {/* User Profile Section */}
              <Grid item xs={12} md={4}>
                <Box sx={{ padding: 3, textAlign: 'center', boxShadow: 3, backgroundColor: '#2222' }}>
                  <Typography
                    variant="h3"
                    align="center"
                    fontSize={{ xs: '1rem', sm: '1.5rem', md: '2rem' }}
                    sx={{
                      backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
                      // mt: 10,
                      letterSpacing: 5,
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                    }}
                    gutterBottom
                  >
                    User Details
                  </Typography>
                  <Avatar
                    alt="User Image"
                    src={userData?.image ? `${API_URL}${userData.image}` : '/default-avatar.png'}
                    sx={{ width: 150, height: 150, margin: 'auto' }}
                  />
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mt: 2, // Margin top for some space between the image and the icons
                  }}>
                    {userData.linkedin && (
                      <a href={userData.linkedin} target="_blank" rel="noopener noreferrer">
                        <IconButton sx={{
                          color: 'white',
                          "&:hover": {
                            transform: "translateY(-3px)",
                            color: '#1E90FF',
                          },
                          mx: 1,
                        }}>
                          <LinkedIn />
                        </IconButton>
                      </a>
                    )}</Box>
                  <Link to={`/public-view-user/${student_id}`} style={{ textDecoration: "none" }}>
                    <Typography variant="h5" sx={{ marginTop: 2, color: 'white', "&:hover": { color: '#1E90FF' }, letterSpacing: 5 }}>
                      {userData?.name ? `${userData.name} ${userData.surname}` : 'No name available'}
                    </Typography></Link>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#444",  // Lighter color for the helper text
                      fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },  // Responsive font size
                      marginTop: '0.5rem',
                      letterSpacing: 2,
                      marginBottom: '1rem',
                      textAlign: 'center',  // Center the helper text
                      fontStyle: 'italic',  // Optional: add some style to make it distinguishable
                    }}
                  >
                    Click on user name to view more info about the user. {/* Customize this text */}
                  </Typography>
                  {userData.facebook && (

                    <Typography variant="body2" sx={{ marginTop: '1rem', color: '#b0bec5', fontSize: { xs: '0.9rem', sm: '1rem' }, letterSpacing: 3 }}>
                      Current Role:  {userData.facebook || 'No description available'}
                    </Typography>
                  )}


                  {userData.youtube && (
                    <div>
                      <YouTubeEmbed
                        videoUrl={userData.youtube}
                      />
                    </div>
                  )}
                  <Typography variant="body2" sx={{ marginTop: 1, letterSpacing: 3 }}>
                    {userData?.about || 'No bio available.'}
                  </Typography>

                  {userData.Experience && (<Box sx={{ width: '100%', marginBottom: 3 }}>
                    <Typography sx={{ color: 'white', marginTop: 2, fontWeight: 'bold', letterSpacing: 3 }}>
                      Time of Availability
                    </Typography>

                    {userData.Experience && (
                      <Typography
                        variant="body2"
                        sx={{
                          marginTop: '0.5rem',
                          color: '#b0bec5',
                          letterSpacing: 2,
                          fontSize: { xs: '0.9rem', sm: '1rem' }, // Responsive font size
                          fontStyle: 'italic', // Italicized for a different style
                          background: 'rgba(255, 255, 255, 0)', // Light background for better contrast
                          padding: '0.5rem 1rem', // Padding for spacing
                          borderRadius: '8px', // Rounded corners for a modern look
                        }}
                      >
                        {userData.Experience || 'N/A'}
                      </Typography>
                    )}
                  </Box>)}

                  {userData.languages && (<Box sx={{ width: '100%', marginBottom: 3 }}>
                    <Typography sx={{ color: 'white', marginTop: 2, fontWeight: 'bold', letterSpacing: 3 }}>
                      Languages Known
                    </Typography>

                    {userData.languages && (
                      <Typography
                        variant="body2"
                        sx={{
                          marginTop: '0.5rem',
                          color: '#b0bec5',
                          letterSpacing: 2,
                          fontSize: { xs: '0.9rem', sm: '1rem' }, // Responsive font size
                          fontStyle: 'italic', // Italicized for emphasis
                          background: 'rgba(255, 255, 255, 0)', // Light background for contrast
                          padding: '0.5rem 1rem', // Padding for spacing
                          borderRadius: '8px', // Rounded corners for modern look
                        }}
                      >
                        {userData.languages || 'N/A'}
                      </Typography>
                    )}
                  </Box>)}

                  {userData.areas_of_interest && (<Box sx={{ width: '100%', marginBottom: 3 }}>
                    <Typography sx={{ color: 'white', marginTop: 2, fontWeight: 'bold', letterSpacing: 3 }}>
                      Areas of Interest
                    </Typography>

                    {userData.areas_of_interest && (
                      <Typography
                        variant="body2"
                        sx={{
                          marginTop: '0.5rem',
                          color: '#b0bec5',
                          letterSpacing: 2,
                          fontSize: { xs: '0.9rem', sm: '1rem' }, // Responsive font size
                          fontStyle: 'italic', // Italicized for emphasis
                          background: 'rgba(255, 255, 255, 0)', // Light background for contrast
                          padding: '0.5rem 1rem', // Padding for spacing
                          borderRadius: '8px', // Rounded corners for modern look
                        }}
                      >
                        {userData.areas_of_interest && userData.areas_of_interest.length > 0
                          ? userData.areas_of_interest.join(', ') // If it's a list, display them as comma-separated values
                          : 'N/A'}
                      </Typography>
                    )}
                  </Box>)}
                  <FeedbackFormRequestFromUser userId={userId} id={id} />
                </Box>


              </Grid>

              <Grid item xs={12} md={8}>
                <Box sx={{
                  padding: { xs: "2rem", sm: "3rem" }, // Responsive padding
                  backgroundColor: "#2222", // Darker background for modern look
                  color: "#fff",
                  borderRadius: "16px",
                  height: "100%",
                  boxSizing: "border-box",
                  boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.3)",
                  },
                }}>
                  <Typography
                    variant="h3"
                    align="center"
                    fontSize={{ xs: '1rem', sm: '1.5rem', md: '2rem' }}
                    sx={{
                      backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
                      // mt: 10,
                      letterSpacing: 5,
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                    }}
                    gutterBottom
                  >
                    Request Details
                  </Typography>

                  <Box sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    mb: 2,
                  }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        color: "#fff",
                        textAlign: "center",
                        fontFamily: "'Helvetica', sans-serif",
                        padding: "0.5rem",
                        letterSpacing: 5,
                        borderRadius: "15px",
                        "&:hover": {
                          color: '#1E90FF',
                          transform: "translateY(-3px)",
                        },
                      }}
                    >
                      {requestData?.section_name} / {requestData?.subsection_name}
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: "bold",
                      marginBottom: "1rem",
                      color: "#fff",
                      letterSpacing: 7,
                      textAlign: "center",
                      fontFamily: "'Helvetica', sans-serif",
                      "&:hover": {
                        color: '#1E90FF',
                      },
                      fontSize: {
                        xs: '1.5rem',   // For extra small screens (mobile)
                        sm: '1.5rem',     // For small screens (tablets)
                        md: '1.5rem',   // For medium screens (laptops)
                        lg: '2rem',     // For large screens (desktops)
                      },
                      marginBottom: {
                        xs: '0.5rem',   // For extra small screens
                        sm: '1rem',     // For small screens
                        md: '1.5rem',   // For medium screens
                        lg: '2rem',     // For large screens
                      },
                    }}
                  >
                    {requestData?.topic}
                  </Typography>


                  <Grid container spacing={3} justifyContent="center" sx={{ mt: 1 }}>

                    {/* <Typography variant="body2" sx={{ fontWeight: "bold", color: "#fff" }}>Language: {language || "N/A"}</Typography> */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "bold",
                        color: "#fff",
                        background: "linear-gradient(45deg, #333333, #555555)",

                        borderRadius: "8px",
                        padding: "6px 12px",
                        mr: 1,
                        mt: 1,
                        letterSpacing: 2,
                        textTransform: "uppercase",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.2)",
                        display: "inline-block",
                        fontSize: "0.875rem", // Smaller size for compactness
                        letterSpacing: "0.8px",
                        transition: "transform 0.3s ease-in-out, background 0.3s ease-in-out",
                        "&:hover": {
                          background: "linear-gradient(45deg, #6e7dff, #6b89ff)",
                          transform: "scale(1.05)",
                        },
                        "@media (max-width: 600px)": {
                          fontSize: "0.75rem", // Make text smaller on mobile
                          padding: "4px 10px", // Adjust padding for smaller screens
                        },
                      }}
                    >
                      Language: {requestData?.language || "N/A"}
                    </Typography>




                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "bold",
                        color: "#fff",
                        background: "linear-gradient(45deg, #333333, #555555)",

                        borderRadius: "8px",
                        padding: "6px 12px",
                        textTransform: "uppercase",
                        mr: 1,
                        mt: 1,
                        letterSpacing: 2,
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.2)",
                        display: "inline-block",
                        fontSize: "0.875rem", // Smaller size for compactness
                        letterSpacing: "0.8px",
                        transition: "transform 0.3s ease-in-out, background 0.3s ease-in-out",
                        "&:hover": {
                          background: "linear-gradient(45deg, #6e7dff, #6b89ff)",
                          transform: "scale(1.05)",
                        },
                        "@media (max-width: 600px)": {
                          fontSize: "0.75rem", // Make text smaller on mobile
                          padding: "4px 10px", // Adjust padding for smaller screens
                        },
                      }}
                    >
                      Date: {requestData?.date || "N/A"}
                    </Typography>


                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "bold",
                        color: "#fff",
                        background: "linear-gradient(45deg, #333333, #555555)",

                        borderRadius: "8px",
                        padding: "6px 12px",
                        textTransform: "uppercase",
                        mr: 1,
                        mt: 1,
                        letterSpacing: 2,
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.2)",
                        display: "inline-block",
                        fontSize: "0.875rem", // Smaller size for compactness
                        letterSpacing: "0.8px",
                        transition: "transform 0.3s ease-in-out, background 0.3s ease-in-out",
                        "&:hover": {
                          background: "linear-gradient(45deg, #6e7dff, #6b89ff)",
                          transform: "scale(1.05)",
                        },
                        "@media (max-width: 600px)": {
                          fontSize: "0.75rem", // Make text smaller on mobile
                          padding: "4px 10px", // Adjust padding for smaller screens
                        },
                      }}
                    >
                      Start Time: {requestData?.time || "N/A"}
                    </Typography>


                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "bold",
                        color: "#fff",
                        background: "linear-gradient(45deg, #333333, #555555)",

                        borderRadius: "8px",
                        padding: "6px 12px",
                        textTransform: "uppercase",
                        mr: 1,
                        mt: 1,
                        letterSpacing: 2,
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.2)",
                        display: "inline-block",
                        fontSize: "0.875rem", // Smaller size for compactness
                        letterSpacing: "0.8px",
                        transition: "transform 0.3s ease-in-out, background 0.3s ease-in-out",
                        "&:hover": {
                          background: "linear-gradient(45deg,  #6e7dff, #6b89ff)",
                          transform: "scale(1.05)",
                        },
                        "@media (max-width: 600px)": {
                          fontSize: "0.75rem", // Make text smaller on mobile
                          padding: "4px 10px", // Adjust padding for smaller screens
                        },
                      }}
                    >
                      Duration: {requestData?.duration} minutes
                    </Typography>



                  </Grid>
                  <Grid container justifyContent="center" alignItems="center" sx={{ padding: { xs: 2, sm: 3, md: 4 }, minHeight: '10vh', backgroundColor: '#1111' }}>
                    <Grid item xs={12} sm={8} md={6} sx={{ backgroundColor: '#1e1e1e', padding: 4, borderRadius: 2, boxShadow: 3 }}>
                      {/* Status */}
                      <Box sx={{ marginBottom: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center', color: '#1E90FF', letterSpacing: 3 }}>
                          Status
                        </Typography>
                        <Typography variant="body1" sx={{ textAlign: 'center', fontSize: { xs: '0.9rem', sm: '1rem' }, color: '#ecf0f1', letterSpacing: 3 }}>
                          <strong>{requestData.status}</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ textAlign: 'center', fontSize: { xs: '0.8rem', sm: '0.9rem' }, color: '#bdc3c7', marginTop: 1, letterSpacing: 3 }}>
                          {requestData.status === 'Accepted' ? (
                            'Your request has been accepted. Proceed with the next steps.'
                          ) : requestData.status === 'cancelled' ? (
                            <span style={{ color: '#e74c3c', letterSpacing: 3, }}>
                              This request has been cancelled. Please contact support if you have any questions.
                            </span>
                          ) : (
                            'Your request is under review. Please wait for further updates.'
                          )}
                        </Typography>

                        {requestData.status === 'pending' && (
                          <Box sx={{ marginTop: 2 }}>



                            <div>
                              <FormControl fullWidth sx={{ marginBottom: 2, letterSpacing: 2 }}>
                                <InputLabel id="platform-select-label" sx={{ letterSpacing: 2 }}>Select Meeting Platform</InputLabel>
                                <Select
                                  labelId="platform-select-label"
                                  value={selectedPlatform}
                                  onChange={handlePlatformChange}
                                  label="Select Meeting Platform"
                                  sx={{ letterSpacing: 2 }}
                                >
                                  <MenuItem value="googleMeet" sx={{ letterSpacing: 2 }}>Google Meet</MenuItem>
                                  <MenuItem value="microsoftTeams" sx={{ letterSpacing: 2 }}>Microsoft Teams</MenuItem>
                                  <MenuItem value="zoom" sx={{ letterSpacing: 2 }}>Zoom</MenuItem>
                                </Select>
                              </FormControl>

                              {renderPlatformInfo()}
                            </div>


                            <TextField
                              label="Paste Meeting Link"
                              variant="outlined"
                              fullWidth
                              sx={{ letterSpacing: 2 }}
                              value={meetingLink}
                              onChange={handleLinkChange}
                              error={isValid === false}
                              helperText={isValid === false && meetingLink.trim() !== "" ? errorMessage : ""}
                            />

                            {isValid === true && meetingLink.trim() !== "" && <p style={{ color: "green", letterSpacing: 2 }}>Link is valid!</p>}
                            {isValid === false && meetingLink.trim() !== "" && <p style={{ color: "red", letterSpacing: 2 }}>Invalid link for the selected platform.</p>}

                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <StyledButton
                                variant="contained"
                                color="primary"
                                sx={{ flex: 1, marginRight: 1, letterSpacing: 4 }}
                                onClick={() => handleAccept(id)}
                              >
                                Accept
                              </StyledButton>
                              <StyledButton
                                variant="outlined"
                                color="secondary"
                                sx={{ flex: 1, letterSpacing: 4 }}
                                onClick={() => handleReject(id)}
                              >
                                Reject
                              </StyledButton>
                            </Box>
                          </Box>
                        )}


                      </Box>
                    </Grid>
                  </Grid>


                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "'Helvetica', sans-serif",
                      fontSize: { xs: "1rem", sm: "1.1rem", md: "1.3rem" },
                      color: '#888',
                      fontWeight: '500',
                      letterSpacing: 3,
                      marginTop: '2.5rem',
                      marginBottom: '1.5rem',
                      textAlign: 'center',  // Add textAlign property to center the text
                    }}
                  >
                    Join for an insightful session on {requestData?.topic}. {/* You can customize this text */}
                  </Typography>
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                      padding: { xs: '16px', sm: '24px', md: '32px' }, // responsive padding
                      textAlign: 'center',
                      width: '100%', // ensures box takes full width
                    }}
                  >
                    {getLogo(requestData.platform) ? (
                      <img
                        src={getLogo(requestData.platform)}
                        alt={requestData.platform}
                        style={{
                          letterSpacing: 3,
                          width: '80px', // default width for small screens
                          height: 'auto', // preserve aspect ratio
                          maxWidth: '100px', // maximum width for larger screens
                        }}
                      />
                    ) : (
                      <span style={{ fontStyle: 'italic', color: 'gray' }}>
                        Meeting Platform Info Not available
                      </span>
                    )}

                    <Typography
                      variant="body1"
                      sx={{
                        marginTop: '12px',
                        letterSpacing: 3,
                        fontSize: { xs: '14px', sm: '16px', md: '18px' }, // responsive font size
                        fontWeight: 500,
                        color: '#333',
                      }}
                    >
                      {getPlatformName(requestData.platform)} {/* Call getPlatformName() properly */}
                    </Typography>
                  </Box>

                  {/* Description */}
                  <Typography
                    variant="body1"
                    sx={{
                      marginBottom: "2rem",
                      lineHeight: 1.8,
                      letterSpacing: 4,
                      color: "#ccc",
                      textAlign: "center",
                      fontFamily: "'Helvetica', sans-serif",
                      fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                      maxWidth: "800px", // Limiting max width for readability
                      margin: "0 auto",  // Center text within its container
                      padding: "0 1rem", // Padding for a spacious feel
                      background: "linear-gradient(135deg, rgba(29, 233, 182, 0.2), rgba(33, 150, 243, 0.2))",
                      borderRadius: "8px",
                      boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        transform: "scale(1.02)",
                        boxShadow: "0px 15px 40px rgba(0, 0, 0, 0.15)",
                        background: "linear-gradient(135deg, rgba(29, 233, 182, 0.2), rgba(33, 150, 243, 0.2))",
                      },
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    }}
                  >
                    {requestData?.description} {/* Use dynamic description data */}
                  </Typography>

                  {/* Helper Text: Additional Session Info */}
                  <Box
                    sx={{
                      maxWidth: '800px',
                      margin: '0 auto',
                      padding: '1rem',
                      background: 'rgba(0, 0, 0, 0.05)',
                      borderRadius: '8px',
                      boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                        color: '#444',
                        letterSpacing: 3,
                        lineHeight: 1.6,
                      }}
                    >
                      {/* Here, you'll learn key concepts about {sessionTopic}. We’ll cover: */}
                      <ul>
                        <li>Introduction to {requestData?.topic}</li>
                        <li>Best practices and tips for mastering {requestData?.topic}</li>
                        <li>Hands-on examples and real-world use cases</li>
                        <li>Interactive session with Q&A and troubleshooting</li>
                      </ul>
                      Don't miss out on an engaging session where I will dive deep into the subject matter!
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, marginTop: 3 }}>





                    {/* Meeting Instructions Button */}
                    <Grid item xs={12} sm="auto" sx={{ textAlign: 'center' }}>
                      <Button
                        variant="contained"
                        color="info"
                        onClick={handleRedirect}
                        sx={{
                          letterSpacing: 3,
                          width: { xs: '100%', sm: 'auto' },  // Full width on small screens, auto on larger screens
                          padding: { xs: '0.75rem 1rem', sm: '0.875rem 1.5rem' },  // Adjust padding for different screen sizes
                          fontSize: { xs: '0.875rem', sm: '1rem' },  // Adjust font size for responsiveness
                          // marginTop: { xs: '1rem', sm: '1.5rem' },  // Adjust margin top for spacing
                        }}
                      >
                        Session Instructions
                      </Button>
                    </Grid>

                    {/* Join Now Button */}
                    <Grid item xs={12} sm="auto" sx={{ textAlign: 'center' }}>
                      <a href="#" onClick={handleJoinClick} style={{ textDecoration: 'none' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          sx={{
                            letterSpacing: 3,
                            width: { xs: '100%', sm: 'auto' },  // Full width on small screens, auto on larger screens
                            padding: { xs: '0.75rem 1rem', sm: '0.875rem 1.5rem' },  // Adjust padding for different screen sizes
                            fontSize: { xs: '0.875rem', sm: '1rem' },  // Adjust font size for responsiveness
                            //  marginTop: { xs: '1rem', sm: '1.5rem' },  // Adjust margin top for spacing
                          }}
                        >
                          Join Now
                        </Button>
                      </a>
                    </Grid>

                    {/* Cancel Session Button */}
                    <Grid item xs={12} sm="auto" sx={{ textAlign: 'center' }}>
                      <CancelSessionRequestUserButton sessionId={id} userId={userId} sx={{ width: { xs: '100%', sm: 'auto' } }} />
                    </Grid>



                  </Box>
                  <GenerateOTPButtonRequestUser sessionId={id} />
                  <Grid container justifyContent="center" alignItems="center" sx={{ padding: { xs: 2, sm: 3, md: 4 }, minHeight: '20vh', backgroundColor: '#1111' }}>
                    <Grid item xs={12} sm={8} md={6} sx={{ backgroundColor: '#1e1e1e', padding: 4, borderRadius: 2, boxShadow: 3 }}>
                      {/* Rating */}
                      <Box sx={{ marginBottom: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center', color: '#ecf0f1', letterSpacing: 3 }}>
                          Rating
                        </Typography>
                        <Typography variant="body1" sx={{ textAlign: 'center', fontSize: { xs: '0.9rem', sm: '1rem' }, color: '#ecf0f1', letterSpacing: 3 }}>
                          <strong>{requestData.student_rating}</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ textAlign: 'center', fontSize: { xs: '0.8rem', sm: '0.9rem' }, color: '#bdc3c7', marginTop: 1, letterSpacing: 3 }}>
                          Based on the overall feedback from students.
                        </Typography>
                      </Box>

                      {/* Comments */}
                      <Box sx={{ marginBottom: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center', color: '#ecf0f1', letterSpacing: 3 }}>
                          Comments
                        </Typography>
                        <Typography variant="body1" sx={{ textAlign: 'center', fontSize: { xs: '0.9rem', sm: '1rem' }, color: '#ecf0f1', letterSpacing: 3 }}>
                          <strong>{requestData.student_comments}</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ textAlign: 'center', fontSize: { xs: '0.8rem', sm: '0.9rem' }, color: '#bdc3c7', marginTop: 1, letterSpacing: 3 }}>
                          Feedback provided by the teacher on the session.
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                PaperProps={{
                  sx: {
                    backgroundColor: '#1e1e1e',
                    color: '#e0e0e0',
                    borderRadius: 2,
                    minWidth: { xs: '90%', sm: 600 },
                    maxHeight: '90vh',
                  },
                }}
              >
                <DialogContent sx={{ pt: 3, pb: 1, px: 3 }}>
                  {/* Embedded Meeting Guidelines */}
                  <MeetingGuidelines />

                  {/* Agreement Checkbox */}
                  <Box sx={{ mt: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={handleCheckboxChange}
                          sx={{
                            color: '#bb86fc',
                            '&.Mui-checked': {
                              color: '#bb86fc',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: '#e0e0e0', letterSpacing: 2 }}>
                          I acknowledge that I have read, understood, and agree to all the terms and conditions outlined above.
                        </Typography>
                      }
                    />
                  </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3 }}>
                  <Button
                    onClick={() => setOpenDialog(false)}
                    variant="outlined"
                    color="secondary"
                    sx={{ letterSpacing: 3 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmAndJoin}
                    variant="contained"
                    color="primary"
                    disabled={!isChecked}
                    sx={{
                      backgroundColor: !isChecked ? '#555' : '#1976d2',
                      letterSpacing: 3,
                      '&:hover': {
                        backgroundColor: !isChecked ? '#555' : '#1565c0',
                      },
                    }}
                  >
                    Confirm and Join
                  </Button>
                </DialogActions>
              </Dialog>


            </Grid>

          )}

        </Box>
      </Box>
      <Footer2 />
    </div>
  );
};

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 5,
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1.5),
  boxShadow: theme.shadows[2],
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

export default RequestFromUserTabDetails;
